export type TrainingSource = 'APPRAISAL' | 'MANUAL' | 'GAP'
export type TrainingPriority = 'LOW' | 'MEDIUM' | 'HIGH'
export type TrainingStatus = 'IDENTIFIED' | 'NOMINATED' | 'SCHEDULED' | 'COMPLETED' | 'DROPPED'

/**
 * Where a need came from.
 *
 * APPRAISAL is written by the Phase 6 appraisal cycle and by nothing today — the receiving
 * side exists and is tested, but no appraisal can complete yet to trigger it. Saying so on
 * the screen is better than a filter value that never matches anything.
 */
export const TRAINING_SOURCE_LABELS: Record<TrainingSource, string> = {
  APPRAISAL: 'From an appraisal',
  MANUAL: 'Raised by hand',
  GAP: 'From a skills-gap analysis',
}

export const TRAINING_PRIORITY_LABELS: Record<TrainingPriority, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
}

export const TRAINING_STATUS_LABELS: Record<TrainingStatus, string> = {
  IDENTIFIED: 'Identified',
  NOMINATED: 'Nominated',
  SCHEDULED: 'Scheduled',
  COMPLETED: 'Completed',
  DROPPED: 'Dropped',
}

/** The states a need can still move to. Closed ones do not reopen — the gap recurs instead. */
export const NEXT_STATUSES: Record<TrainingStatus, TrainingStatus[]> = {
  IDENTIFIED: ['NOMINATED', 'DROPPED'],
  NOMINATED: ['SCHEDULED', 'DROPPED'],
  SCHEDULED: ['COMPLETED', 'DROPPED'],
  COMPLETED: [],
  DROPPED: [],
}

export interface TrainingNeedView {
  id: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  competencyLabel: string
  competencyRef: string | null
  source: TrainingSource
  sourceRef: string | null
  priority: TrainingPriority
  status: TrainingStatus
  targetDate: string | null
  notes: string | null
  identifiedAt: string
  closedAt: string | null
}

// --- Phase 6 part 3: the catalogue ---

export type SessionStatus = 'PLANNED' | 'OPEN' | 'RUNNING' | 'COMPLETED' | 'CANCELLED'

export type NominationStatus =
  | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'WITHDRAWN' | 'ATTENDED' | 'NO_SHOW'

export type DeliveryMode = 'INTERNAL' | 'EXTERNAL' | 'ONLINE' | 'CERTIFICATION'

export type NominationSource = 'MANUAL' | 'TRAINING_NEED' | 'SELF'

export const SESSION_STATUS_LABELS: Record<SessionStatus, string> = {
  PLANNED: 'Planned',
  OPEN: 'Open for nominations',
  RUNNING: 'Running',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
}

export const NOMINATION_STATUS_LABELS: Record<NominationStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Awaiting approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
  ATTENDED: 'Attended',
  NO_SHOW: 'Did not attend',
}

export const DELIVERY_LABELS: Record<DeliveryMode, string> = {
  INTERNAL: 'Delivered internally',
  EXTERNAL: 'External provider',
  ONLINE: 'Online',
  CERTIFICATION: 'Leads to a certification',
}

export const NOMINATION_SOURCE_LABELS: Record<NominationSource, string> = {
  MANUAL: 'Nominated by somebody',
  TRAINING_NEED: 'From a training need',
  SELF: 'Asked for it themselves',
}

export interface TrainingCourseView {
  id: string
  legalEntityId: string | null
  code: string
  title: string
  description: string | null
  deliveryMode: string
  provider: string | null
  competencyId: string | null
  competencyName: string | null
  durationHours: number | null
  /** Null means this course issues no certificate. */
  certificateDocumentTypeId: string | null
  certificateDocumentTypeName: string | null
  /** Null means a certificate from this course does not expire. */
  certificateValidMonths: number | null
  /** No default: what counts as having done the course is the employer's judgement. */
  minimumAttendancePercent: number | null
  active: boolean
  sessionCount: number
  certificateNote: string
}

export interface TrainingSessionView {
  id: string
  legalEntityId: string
  courseId: string
  courseTitle: string | null
  reference: string
  title: string | null
  startsOn: string
  endsOn: string
  location: string | null
  trainer: string | null
  /** Null means uncapped. */
  capacity: number | null
  totalCost: number | null
  costPerHead: number | null
  currency: string | null
  status: string
  completedAt: string | null
  cancellationReason: string | null
  notes: string | null
  nominated: number
  placesTaken: number
  placesLeft: number | null
}

export interface SessionAttendanceView {
  id: string
  nominationId: string
  attendedOn: string
  present: boolean
  hours: number | null
  note: string | null
}

export interface TrainingEvaluationView {
  id: string
  nominationId: string
  reactionScore: number | null
  reactionComments: string | null
  reactedOn: string | null
  /** Null reads as "not yet asked", never as a rating of zero. */
  followupScore: number | null
  followupComments: string | null
  followupOn: string | null
  followedUp: boolean
}

export interface TrainingNominationView {
  id: string
  sessionId: string
  sessionReference: string | null
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  trainingNeedId: string | null
  source: string
  status: string
  submittedAt: string | null
  decidedAt: string | null
  decisionComment: string | null
  /** False when no training budget covered this entity and year. */
  budgetChecked: boolean
  budgetAmount: number | null
  budgetSpent: number | null
  budgetOverage: number | null
  budgetNote: string | null
  attendancePercent: number | null
  certificateDocumentId: string | null
  /** Why there is no certificate, when there is none. */
  certificateNote: string | null
  attendance: SessionAttendanceView[]
  evaluation: TrainingEvaluationView | null
}

export interface SessionCompletionView {
  attended: number
  noShow: number
  certificatesIssued: number
  needsClosed: number
  note: string | null
}

export interface TrainingBudgetView {
  id: string
  legalEntityId: string
  departmentId: string | null
  departmentName: string | null
  fiscalYear: number
  budgetAmount: number
  currency: string
  notes: string | null
  committed: number
  remaining: number
}

/** What an approver sees on a training nomination. */
export interface TrainingNominationDecisionContext {
  nomination: TrainingNominationView
  session: TrainingSessionView
  course: TrainingCourseView
  /** Why this person was put forward, when it came from an appraisal. */
  needReason: string | null
  /** Null when no budget covers this entity and year. */
  budget: TrainingBudgetView | null
  budgetNote: string | null
  /** What this one place costs, or null when the session prices nothing. */
  costOfThisPlace: number | null
}
